/**
 * Правила вложений — как на сервере (§14.2): список закрытый, исполняемых
 * типов в нём нет. Проверяются до загрузки: отказ после 25 МБ трафика
 * бесполезен, а на телефоне ещё и платный.
 */

/** Записи экрана из формы задачи — у них свой, больший предел. */
export const RECORDING_TYPES: readonly string[] = ["video/webm", "video/mp4"];

export const ALLOWED_TYPES: readonly string[] = [
  "image/png",
  "image/jpeg",
  "image/gif",
  "image/webp",
  "application/pdf",
  "text/plain",
  "text/csv",
  "application/msword",
  "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
  "application/vnd.ms-excel",
  "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
  "application/zip",
  // Журнал сетевых запросов (HAR) к записи экрана.
  "application/json",
  ...RECORDING_TYPES,
];

export const MAX_BYTES = 25 * 1024 * 1024;
/** Как MAX_RECORDING_BYTES сервера: около 25 минут записи при 1 Мбит/с. */
export const MAX_RECORDING_BYTES = 200 * 1024 * 1024;

/** Тип файла для проверки и загрузки. У HAR браузер типа не знает и отдаёт
 *  пустую строку — по содержимому это JSON, им он и уходит на сервер. */
export function attachmentMime(file: { type: string; name?: string }): string {
  if (file.type === "" && file.name !== undefined && /\.(har|json)$/i.test(file.name)) {
    return "application/json";
  }
  // MediaRecorder отдаёт тип с кодеками: «video/webm;codecs=vp9».
  return file.type.split(";")[0]?.trim() ?? "";
}

export function maxBytesFor(mime: string): number {
  return RECORDING_TYPES.includes(mime) ? MAX_RECORDING_BYTES : MAX_BYTES;
}

/** Причина отказа или null. */
export function attachmentProblem(file: {
  type: string;
  size: number;
  name?: string;
}): string | null {
  const mime = attachmentMime(file);
  if (!ALLOWED_TYPES.includes(mime)) return "Такой тип файла загружать нельзя";
  const limit = maxBytesFor(mime);
  if (file.size > limit) return `Файл больше ${String(limit / 1024 / 1024)} МБ`;
  return null;
}

/** Типы, которые сервер отдаёт inline как изображение: у них есть превью. */
const PREVIEW_IMAGE_TYPES: ReadonlySet<string> = new Set([
  "image/png",
  "image/jpeg",
  "image/gif",
  "image/webp",
]);

export function isPreviewableImage(file: { mime: string }): boolean {
  return PREVIEW_IMAGE_TYPES.has(file.mime);
}

/** Видео сервер тоже отдаёт inline — его можно смотреть в карточке. */
export function isPlayableVideo(file: { mime: string }): boolean {
  return RECORDING_TYPES.includes(file.mime);
}
